import { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { t } from "i18next";

import styles from "./Pricing.module.css";
import { activeFreePlan, upgradeSubscription, downgradeSubscription } from "../../../services/paymentService";
import { fetchUserByToken } from "../../../app/features/userSlice";

// Import refactored components
import PricingHeader from "./PricingHeader";
import PricingTabs from "./PricingTabs";
import PlanCard from "./PlanCard";
import FeaturesList from "./FeaturesList";
import ConfirmationModals from "./ConfirmationModals";

// Import utility functions
import { createMonthlyPlans, createAnnualPlans } from "./PlanUtils";
import { isCycleSwitchPlan, determineActivePlan } from "./SubscriptionLogic";

function Pricing({ handleActiveFreePlan, onSeeAllPlans }) {
    const [activeTab, setActiveTab] = useState("monthly");
    const [cardHeight, setCardHeight] = useState(0);
    const [activePlan, setActivePlan] = useState(0);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showDowngradeModal, setShowDowngradeModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [loadingPlanId, setLoadingPlanId] = useState(null);
    const [isCycleSwitch, setIsCycleSwitch] = useState(false);

    const cardRef = useRef(null);
    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Set active plan based on user subscription
    useEffect(() => {
        setActivePlan(determineActivePlan(user, activeTab));
    }, [user?.activeSubscriptionPriceId, activeTab, user]);

    // Get plans based on active tab
    const monthlyPlans = createMonthlyPlans(user);
    const annualPlans = createAnnualPlans(user);
    const plans = activeTab === "monthly" ? monthlyPlans : annualPlans;

    // Set card height for consistent UI
    useEffect(() => {
        if (cardRef.current) {
            setCardHeight(cardRef.current.offsetHeight);
        }
    }, []);

    // Check if user is an employer with autoRenewalStatus set to false
    const isEmployerWithAutoRenewalOff = 
        user?.account_type === 'employer' && 
        user?.subscriptionDetail?.cancel_at_period_end === true;

    const handleUpgrade = (plan) => {
        // If user is an employer with autoRenewalStatus set to false, do nothing
        if (isEmployerWithAutoRenewalOff) {
            return;
        }

        setSelectedPlan(plan);
        setIsCycleSwitch(false);
        setShowUpgradeModal(true);
    };

    const handleDowngrade = (plan) => {
        // If user is an employer with autoRenewalStatus set to false, do nothing
        if (isEmployerWithAutoRenewalOff) {
            return;
        }
        
        setSelectedPlan(plan);
        
        // For free plan, show cancel subscription modal
        if (plan.name === t('employer_plan.Free')) {
            setShowDowngradeModal(true);
            return;
        }
        
        // For different types of downgrades
        const isSMEToBasicSameCycle = (
            user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH &&
            plan.priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH
        ) || (
            user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR &&
            plan.priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR
        );
        
        const isAnnualSMEToMonthlyDowngrade = 
            user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_YEAR &&
            (plan.priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH ||
             plan.priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_MONTH);
             
        const isMonthlySMEToAnnualBasic = 
            user?.activeSubscriptionPriceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_SME_FOR_1_MONTH &&
            plan.priceId === process.env.REACT_APP_STRIPE_SUB_PRICE_ID_RECURRING_BASIC_FOR_1_YEAR;
            
        if (isSMEToBasicSameCycle || isAnnualSMEToMonthlyDowngrade || isMonthlySMEToAnnualBasic) {
            setShowDowngradeModal(true);
        }
    };

    const handleProceedUpgrade = () => {
        setLoadingPlanId(selectedPlan.priceId);
        setShowUpgradeModal(false);
        
        if (isCycleSwitch && user?.subscriptionStatus?.toUpperCase() === 'ACTIVE') {
            // For cycle switch plans, first cancel current subscription then upgrade
            if (typeof handleActiveFreePlan === 'function') {
                activeFreePlan({ token: userToken })
                    .then(() => {
                        upgradeSubscription(userToken, selectedPlan.priceId)
                            .then(response => {
                                dispatch(fetchUserByToken(userToken));
                                setLoadingPlanId(null);
                                if (response.clientSecret) {
                                    navigate('/profile/billing/payment-confirmation', {
                                        state: {
                                            clientSecret: response.clientSecret,
                                            subscriptionId: response.subscriptionId
                                        }
                                    });
                                } else {
                                    navigate('/profile/billing');
                                }
                            })
                            .catch(error => {
                                console.error(error);
                                setLoadingPlanId(null);
                            });
                    })
                    .catch(error => {
                        console.error(error);
                        setLoadingPlanId(null);
                    });
            }
        } else {
            // Regular upgrade process
            upgradeSubscription(userToken, selectedPlan.priceId)
                .then(response => {
                    dispatch(fetchUserByToken(userToken));
                    setLoadingPlanId(null);
                    if (response.clientSecret) {
                        navigate('/profile/billing/payment-confirmation', {
                            state: {
                                clientSecret: response.clientSecret,
                                subscriptionId: response.subscriptionId
                            }
                        });
                    } else {
                        navigate('/profile/billing');
                    }
                })
                .catch(error => {
                    console.error(error);
                    setLoadingPlanId(null);
                });
        }
    };

    const handleProceedDowngrade = () => {
        setLoadingPlanId(selectedPlan.priceId);
        setShowDowngradeModal(false);

        if (selectedPlan.priceId === 'free') {
            // Navigate to discount-offer page instead of immediately activating the free plan
            navigate('/discount-offer');
        } else {
            // For Basic plan downgrade
            downgradeSubscription(userToken, selectedPlan.priceId)
                .then(() => {
                    dispatch(fetchUserByToken(userToken));
                    setLoadingPlanId(null);
                    navigate('/profile/billing');
                })
                .catch(error => {
                    console.error(error);
                    setLoadingPlanId(null);
                });
        }
    };

    const handleCycleSwitchPlan = (plan) => {
        // If user is an employer with autoRenewalStatus set to false, do nothing
        if (isEmployerWithAutoRenewalOff) {
            return;
        }
        
        setLoadingPlanId(plan.priceId);
        setSelectedPlan(plan);
        setIsCycleSwitch(true);
        setShowUpgradeModal(true);
    };

    const handlePlanAction = (plan) => {
        // If user is an employer with autoRenewalStatus set to false, do nothing
        if (isEmployerWithAutoRenewalOff) {
            return;
        }
        
        // Check if this is a cycle switch (monthly to annual or vice versa)
        const isCycleSwitch = isCycleSwitchPlan(user, plan);
        
        if (isCycleSwitch && user?.subscriptionStatus?.toUpperCase() === 'ACTIVE') {
            handleCycleSwitchPlan(plan);
        } 
        else if (plan.buttonText === t('pricing.upgrade')) {
            handleUpgrade(plan);
        } 
        else if (plan.buttonText === t('pricing.downgrade')) {
            handleDowngrade(plan);
        } 
        else if (plan.name === t('employer_plan.Free') && plan.buttonText !== 'active') {
            handleActiveFreePlan(true);
        } 
        else if (plan.buttonText !== 'active') {
            // Default action for other plans
            window.location.href = plan.link + '?prefilled_email=' + user?.email;
        }
    };

    return (
        <div className={styles.wrapper}>
            <Helmet>
                <title>FindCV - Pricing</title>
            </Helmet>
            
            <div className={styles.container}>
                {/* Header with back button */}
                <PricingHeader onSeeAllPlans={onSeeAllPlans} />
                
                {/* Monthly/Annually tabs */}
                <PricingTabs activeTab={activeTab} setActiveTab={setActiveTab} />
                
                {/* Plans display */}
                <div className={styles.plans}>
                    {plans.map((plan, index) => (
                        index === 0 ? (
                             <></>
                        ) : (
                            <PlanCard
                                key={index}
                                plan={plan}
                                index={index}
                                activePlan={activePlan}
                                handlePlanAction={handlePlanAction}
                                isLoading={loadingPlanId === plan.priceId}
                                ref={index === 1 ? cardRef : null}
                            />
                        )
                    ))}
                </div>
            </div>

            {/* Confirmation Modals */}
            <ConfirmationModals 
                showUpgradeModal={showUpgradeModal}
                showDowngradeModal={showDowngradeModal}
                selectedPlan={selectedPlan}
                setShowUpgradeModal={setShowUpgradeModal}
                setShowDowngradeModal={setShowDowngradeModal}
                handleProceedUpgrade={handleProceedUpgrade}
                handleProceedDowngrade={handleProceedDowngrade}
            />
        </div>
    );
}

export default Pricing;
